import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Filter } from 'lucide-react';
import {
    CarTripCard, Pagination,
} from '../../services/CarTripService';
import type { CarTrip } from '../../types/CarTripTypes';
import { getAllTripTags } from '../../services/api/flightsApi';
import { FilterChipsInput } from '../common/FilterChipsInput';
import type { TranslationKeys } from '../../translations';
import { filterAndSortCarTrips } from '../../utils/carTripUi';

const CAR_TRIPS_PAGE_SIZE = 10;

export type CarTripsTabPanelProps = {
    t: TranslationKeys;
    allTrips: CarTrip[];
    carTrips: CarTrip[];
    currentPage: number;
    handlePageChange: (page: number) => void;
    prevPage: () => void;
    nextPage: () => void;
    deleteCarTrip: (id: number) => void;
    viewCarTrip: (trip: CarTrip) => void;
    editCarTrip: (trip: CarTrip) => void;
    editCarTripAddDay: (trip: CarTrip) => void;
    isAuthenticated: boolean;
    onAdd: () => void;
};

export const CarTripsTabPanel: React.FC<CarTripsTabPanelProps> = ({
    t,
    allTrips,
    currentPage,
    handlePageChange,
    prevPage,
    nextPage,
    deleteCarTrip,
    viewCarTrip,
    editCarTrip,
    editCarTripAddDay,
    isAuthenticated,
    onAdd,
}) => {
    const [showCarFilter, setShowCarFilter] = useState(false);
    const [carFilterChips, setCarFilterChips] = useState<string[]>([]);
    const [carFilterApplied, setCarFilterApplied] = useState<string[]>([]);
    const [carTags, setCarTags] = useState<string[]>([]);
    const [selectedCarTripId, setSelectedCarTripId] = useState<number | null>(null);

    useEffect(() => {
        getAllTripTags().then(setCarTags).catch(() => setCarTags([]));
    }, []);

    const carFilterSuggestions = useMemo(() => {
        const y = new Date().getFullYear();
        const years = [String(y), String(y - 1), String(y - 2), String(y - 3)];
        return [...years, ...carTags].filter((v, i, a) => a.indexOf(v) === i);
    }, [carTags]);

    const filteredCarTrips = useMemo(
        () => filterAndSortCarTrips(allTrips ?? [], carFilterApplied, carTags),
        [allTrips, carFilterApplied, carTags],
    );

    const carTripsFilteredTotal = filteredCarTrips.length;
    const carTripsDisplay = useMemo(() => {
        const start = (currentPage - 1) * CAR_TRIPS_PAGE_SIZE;
        return filteredCarTrips.slice(start, start + CAR_TRIPS_PAGE_SIZE);
    }, [filteredCarTrips, currentPage]);

    const carFilterActiveCount = carFilterApplied.length;
    const applyCarFilter = () => {
        setCarFilterApplied([...carFilterChips]);
        handlePageChange(1);
        setShowCarFilter(false);
    };
    const resetCarFilter = () => {
        setCarFilterChips([]);
        setCarFilterApplied([]);
        handlePageChange(1);
    };

    return (
        <div className="space-y-2">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <button
                        type="button"
                        onClick={() => setShowCarFilter(!showCarFilter)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors ${showCarFilter || carFilterActiveCount > 0 ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        title={t.filter}
                    >
                        <Filter className="h-4 w-4" />
                        {t.filter}
                        {carFilterActiveCount > 0 && <span className="text-xs bg-amber-600 text-white px-1.5 rounded">{carFilterActiveCount}</span>}
                    </button>
                    {showCarFilter && (
                        <div className="mt-2 p-3 rounded-2xl border border-amber-200/80 dark:border-slate-600 bg-gradient-to-br from-amber-50/90 to-white dark:from-slate-800/80 dark:to-slate-900/60 space-y-2.5">
                            <p className="text-[11px] text-stone-500 dark:text-slate-400">{t.filterChipsHint}</p>
                            <FilterChipsInput
                                chips={carFilterChips}
                                onChange={setCarFilterChips}
                                suggestions={carFilterSuggestions}
                                placeholder={t.filterChipsPlaceholder}
                            />
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={applyCarFilter} className="px-3.5 py-2 rounded-xl text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 shadow-sm">
                                    {t.applyFilter}
                                </button>
                                <button type="button" onClick={resetCarFilter} className="px-3.5 py-2 rounded-xl text-sm border border-stone-300 dark:border-slate-600 text-stone-700 dark:text-slate-300 hover:bg-stone-100 dark:hover:bg-slate-700">
                                    {t.resetFilter}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onAdd}
                    className="hidden md:inline-flex items-center justify-center shrink-0 min-h-9 min-w-9 px-2.5 py-1.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-sm"
                    title={t.addTrip}
                    aria-label={t.addTrip}
                >
                    <Plus className="h-4 w-4" />
                </button>
            </div>
            {carTripsDisplay.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-300 dark:border-slate-600 px-6 py-12 text-center text-sm text-stone-500 dark:text-slate-400">
                    {carFilterActiveCount > 0 ? (t.noFilterResults || 'Ничего не найдено') : t.carsEmpty}
                    {carFilterActiveCount > 0 && (
                        <div className="mt-3">
                            <button type="button" onClick={resetCarFilter} className="text-sm text-amber-700 hover:underline">{t.resetFilter}</button>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {carTripsDisplay.map(trip => (
                        <CarTripCard
                            key={trip.id}
                            trip={trip}
                            selected={trip.id === selectedCarTripId}
                            onSelect={() => setSelectedCarTripId(prev => (prev === trip.id ? null : trip.id))}
                            onDelete={deleteCarTrip}
                            onView={viewCarTrip}
                            onEdit={editCarTrip}
                            onAddDay={editCarTripAddDay}
                            isAuthenticated={isAuthenticated}
                        />
                    ))}
                    {carTripsFilteredTotal > CAR_TRIPS_PAGE_SIZE && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.max(1, Math.ceil(carTripsFilteredTotal / CAR_TRIPS_PAGE_SIZE))}
                            onPageChange={handlePageChange}
                            onPrevPage={prevPage}
                            onNextPage={nextPage}
                        />
                    )}
                </>
            )}
        </div>
    );
};
